import { useEffect, useRef } from "react";
import { createPreloadTracker, useLatencySample } from "./useScrollPreload";

// A page list keeps its existing onScroll handler for desktop/PWA. Mobile
// browser pages use the document instead, so observe the list's visible edge.
export default function useDocumentScrollPagination({ enabled, loading, onLoadMore }) {
  const listRef = useRef(null);
  const stateRef = useRef({ enabled, onLoadMore, requested: false });
  const trackerRef = useRef(null);
  if (!trackerRef.current) {
    trackerRef.current = createPreloadTracker();
  }

  useEffect(() => {
    stateRef.current.enabled = enabled;
    stateRef.current.onLoadMore = onLoadMore;
    if (!enabled) stateRef.current.requested = false;
  }, [enabled, onLoadMore]);

  useLatencySample(trackerRef.current, loading ?? !enabled);

  useEffect(() => {
    const handleScroll = () => {
      const state = stateRef.current;
      const list = listRef.current;
      if (!list || !list.getClientRects().length
        || !list.closest("main[data-mobile-document-scroll]")) return;

      const tracker = trackerRef.current;
      const now = performance.now();
      const rect = list.getBoundingClientRect();
      tracker.observe(-rect.top, now);
      if (!state.enabled || state.requested) return;

      const viewport = window.visualViewport;
      const viewportHeight = viewport ? viewport.height : window.innerHeight;
      const visibleBottom = viewport
        ? viewport.offsetTop + viewport.height : window.innerHeight;
      if (rect.bottom < visibleBottom + tracker.threshold(viewportHeight)) {
        state.requested = true;
        tracker.markRequested(now);
        state.onLoadMore();
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return listRef;
}
